/**
 * Lokale Arbeitskopie ("Draft") des Kundeneditors.
 *
 * Verbindliche Save-Policy (siehe .editors/ZIELVORSCHLAG.md):
 * - kein Auto-Save,
 * - Feldänderungen verändern ausschließlich die lokale Arbeitskopie,
 * - erst "Speichern und schließen" schreibt zum Server,
 * - "Änderungen verwerfen" stellt den Ausgangszustand vollständig her,
 * - bei einem Save-Fehler bleibt der Draft erhalten.
 *
 * Bewusst als reines Modul ohne Vue-Import, damit die Dirty-Erkennung ohne
 * Browser getestet werden kann.
 */

/**
 * Tiefe Kopie der beim Öffnen geladenen Werte.
 *
 * Notwendig für alles, was ein Feld verändern kann:
 * - Objekte (content, info)
 * - Listen (Repeater, Bilderlisten)
 * - verschachtelte Objekte innerhalb von Listen
 *
 * Primitive Werte werden direkt übernommen. Date/RegExp kommen in
 * Editor-Inhalten nicht vor - Inhalte sind reines JSON.
 */
export function deepCopy(value) {
    if (value === null || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.map(deepCopy);

    const out = {};
    for (const key of Object.keys(value)) {
        out[key] = deepCopy(value[key]);
    }

    return out;
}

/**
 * Stabile, reihenfolgeunabhängige Serialisierung für den Dirty-Vergleich.
 *
 * Objektschlüssel werden sortiert: Eine reine Umsortierung im JSON (die etwa
 * beim Setzen eines neuen Schlüssels über JSONPath entstehen kann) ist keine
 * inhaltliche Änderung und darf den Editor nicht als geändert markieren.
 *
 * Listenreihenfolge bleibt bedeutungstragend - Sortieren IST eine Änderung.
 */
export function stableStringify(value) {
    if (value === undefined) return 'undefined';
    if (value === null || typeof value !== 'object') return JSON.stringify(value);

    if (Array.isArray(value)) {
        return `[${value.map(stableStringify).join(',')}]`;
    }

    const keys = Object.keys(value).sort();

    return `{${keys.map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
}

/**
 * Unterscheiden sich zwei Werte inhaltlich?
 */
export function valuesDiffer(a, b) {
    return stableStringify(a) !== stableStringify(b);
}

/**
 * Ausgangszustand beim Öffnen des Editors.
 *
 * @param {{label: ?string, content: Object, info: Object, custom_css: ?string}} loaded
 */
export function createOriginal(loaded) {
    return {
        label: loaded?.label ?? null,
        content: deepCopy(loaded?.content ?? {}),
        info: deepCopy(loaded?.info ?? {}),
        custom_css: loaded?.custom_css ?? null,
    };
}

/**
 * Welche fachlichen Bereiche wurden geändert?
 *
 * @param {Object} original
 * @param {{label: ?string, content: Object, info: Object, custom_css: ?string}} draft
 * @returns {{label: boolean, content: boolean, info: boolean, custom_css: boolean}}
 */
export function dirtyFields(original, draft) {
    if (! original) {
        return { label: false, content: false, info: false, custom_css: false };
    }

    return {
        label: valuesDiffer(original.label ?? null, draft?.label ?? null),
        content: valuesDiffer(original.content ?? {}, draft?.content ?? {}),
        info: valuesDiffer(original.info ?? {}, draft?.info ?? {}),
        custom_css: valuesDiffer(original.custom_css ?? null, draft?.custom_css ?? null),
    };
}

/**
 * Gibt es ungespeicherte Änderungen?
 */
export function isDirty(original, draft) {
    return Object.values(dirtyFields(original, draft)).some(Boolean);
}

/**
 * Die geänderten Bereiche, als Sprachschlüssel.
 *
 * Schlüssel statt Text: Dieses Modul läuft auch in Node und kennt die
 * Sprache des Panels nicht. Übersetzt wird beim Anzeigen (StepEditStore,
 * Getter `dirtySummary`), dann zieht die Liste beim Sprachwechsel mit.
 *
 * @see lang/de_de/ui/editor.json -> step.areas
 */
export const DIRTY_AREA_KEYS = {
    label: 'editor.step.areas.label',
    content: 'editor.step.areas.content',
    info: 'editor.step.areas.info',
    custom_css: 'editor.step.areas.custom_css',
};

export function dirtySummary(original, draft) {
    const labels = DIRTY_AREA_KEYS;

    return Object.entries(dirtyFields(original, draft))
        .filter(([, changed]) => changed)
        .map(([field]) => labels[field]);
}

/**
 * Draft auf den Ausgangszustand zurücksetzen.
 *
 * Gibt frische tiefe Kopien zurück - der Ausgangszustand selbst bleibt
 * unangetastet und kann erneut zum Verwerfen verwendet werden.
 */
export function restoreOriginal(original) {
    return {
        label: original?.label ?? null,
        content: deepCopy(original?.content ?? {}),
        info: deepCopy(original?.info ?? {}),
        custom_css: original?.custom_css ?? null,
    };
}

/**
 * Nutzlast für genau EINEN fachlichen Save.
 *
 * Es wird immer der vollständige Draft gesendet - kein Teil-Save je Feld und
 * kein zweiter Request für "info".
 */
export function buildSavePayload(draft) {
    return {
        label: draft?.label ?? null,
        content: draft?.content ?? {},
        info: draft?.info ?? {},
    };
}
